// ============================================================
// Chess Arena — Board Evaluator
// ============================================================
// Scores a board position from the perspective of a given team.
// Combines material, king safety, threats, defense, board
// control, and piece advancement into a single numeric score.
// ============================================================

import {
  GridPos,
  PieceType,
  Team,
  BOARD_COLS,
  BOARD_ROWS,
} from '../core/types';
import { BoardSnapshot, SnapPiece } from './minimaxSearch';

// -----------------------------------------------------------------
// Piece values — standard chess-relative, tuned for our game
// -----------------------------------------------------------------

const PIECE_VALUES: Record<PieceType, number> = {
  King: 0,    // King is priceless — evaluated via safety instead
  Pawn: 100,
  Knight: 320,
  Bishop: 330,
  Rook: 500,
  Queen: 900,
};

// -----------------------------------------------------------------
// Weights for each evaluation component
// -----------------------------------------------------------------

interface EvalWeights {
  material: number;
  kingSafety: number;
  threats: number;
  defense: number;
  advancement: number;
  centerControl: number;
  mobility: number;
}

const DEFAULT_WEIGHTS: EvalWeights = {
  material: 1.0,
  kingSafety: 1.5,
  threats: 0.8,
  defense: 0.4,
  advancement: 0.15,
  centerControl: 0.1,
  mobility: 0.05,
};

// -----------------------------------------------------------------
// Attack map helpers
// -----------------------------------------------------------------

/** Get all cells a piece attacks (for threat/defense calculation). */
function getAttackedCells(piece: SnapPiece, snap: BoardSnapshot): GridPos[] {
  const cells: GridPos[] = [];
  const { col, row } = piece.pos;

  switch (piece.type) {
    case 'Pawn': {
      // Pawns attack diagonally
      for (const dc of [-1, 1]) {
        for (const dr of [-1, 1]) {
          const t = { col: col + dc, row: row + dr };
          if (isValid(t)) cells.push(t);
        }
      }
      break;
    }
    case 'Knight': {
      const offsets = [
        [-2, -1], [-2, 1], [2, -1], [2, 1],
        [-1, -2], [1, -2], [-1, 2], [1, 2],
      ];
      for (const [dc, dr] of offsets) {
        const t = { col: col + dc, row: row + dr };
        if (isValid(t) && !isRock(t, snap)) cells.push(t);
      }
      break;
    }
    case 'King': {
      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          const t = { col: col + dc, row: row + dr };
          if (isValid(t) && !isRock(t, snap)) cells.push(t);
        }
      }
      break;
    }
    case 'Bishop':
      addSlidingAttacks(cells, piece.pos, [[-1,-1],[1,-1],[-1,1],[1,1]], snap);
      break;
    case 'Rook':
      addSlidingAttacks(cells, piece.pos, [[0,-1],[0,1],[-1,0],[1,0]], snap);
      break;
    case 'Queen':
      addSlidingAttacks(cells, piece.pos, [
        [-1,-1],[1,-1],[-1,1],[1,1],[0,-1],[0,1],[-1,0],[1,0],
      ], snap);
      break;
  }
  return cells;
}

function addSlidingAttacks(
  cells: GridPos[],
  origin: GridPos,
  dirs: number[][],
  snap: BoardSnapshot,
): void {
  for (const [dc, dr] of dirs) {
    let c = origin.col + dc;
    let r = origin.row + dr;
    let steps = 0;
    while (isValid({ col: c, row: r }) && steps < 4) {
      if (isRock({ col: c, row: r }, snap)) break;
      cells.push({ col: c, row: r });
      // Stop after hitting any piece (but the cell is still attacked)
      if (snap.pieceAt(c, r)) break;
      c += dc;
      r += dr;
      steps++;
    }
  }
}

function isValid(pos: GridPos): boolean {
  return pos.col >= 0 && pos.col < BOARD_COLS && pos.row >= 0 && pos.row < BOARD_ROWS;
}

function isRock(pos: GridPos, snap: BoardSnapshot): boolean {
  return snap.terrainAt(pos.col, pos.row) === 'rock';
}

// -----------------------------------------------------------------
// Build attack maps for both teams
// -----------------------------------------------------------------

interface AttackMaps {
  /** Set of "col,row" strings attacked by each team */
  playerAttacks: Set<string>;
  enemyAttacks: Set<string>;
  /** Count of attackers per cell */
  playerAttackCount: Map<string, number>;
  enemyAttackCount: Map<string, number>;
}

function buildAttackMaps(snap: BoardSnapshot): AttackMaps {
  const playerAttacks = new Set<string>();
  const enemyAttacks = new Set<string>();
  const playerAttackCount = new Map<string, number>();
  const enemyAttackCount = new Map<string, number>();

  for (const piece of snap.pieces) {
    const attacked = getAttackedCells(piece, snap);
    const set = piece.team === 'player' ? playerAttacks : enemyAttacks;
    const countMap = piece.team === 'player' ? playerAttackCount : enemyAttackCount;

    for (const cell of attacked) {
      const key = `${cell.col},${cell.row}`;
      set.add(key);
      countMap.set(key, (countMap.get(key) ?? 0) + 1);
    }
  }

  return { playerAttacks, enemyAttacks, playerAttackCount, enemyAttackCount };
}

// -----------------------------------------------------------------
// Evaluation components
// -----------------------------------------------------------------

/** Material balance: sum of piece values. */
function evalMaterial(snap: BoardSnapshot, team: Team): number {
  let score = 0;
  for (const piece of snap.pieces) {
    if (piece.type === 'King') continue; // King has no material value
    const value = PIECE_VALUES[piece.type];
    score += piece.team === team ? value : -value;
  }
  return score;
}

/** King safety: penalize when king is in enemy attack zone. */
function evalKingSafety(
  snap: BoardSnapshot,
  team: Team,
  attacks: AttackMaps,
): number {
  let score = 0;
  const enemyAttackSet = team === 'player' ? attacks.enemyAttacks : attacks.playerAttacks;
  const enemyCountMap = team === 'player' ? attacks.enemyAttackCount : attacks.playerAttackCount;
  const friendlyAttackSet = team === 'player' ? attacks.playerAttacks : attacks.enemyAttacks;

  for (const piece of snap.pieces) {
    if (piece.type !== 'King') continue;
    const key = `${piece.pos.col},${piece.pos.row}`;

    if (piece.team === team) {
      // Our king — penalize if under attack
      if (enemyAttackSet.has(key)) {
        const attackerCount = enemyCountMap.get(key) ?? 1;
        score -= 200 * attackerCount;
      }
      // Penalize exposed king (count attacked squares around king)
      let dangerousNeighbors = 0;
      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          const nk = `${piece.pos.col + dc},${piece.pos.row + dr}`;
          if (enemyAttackSet.has(nk)) dangerousNeighbors++;
        }
      }
      score -= 15 * dangerousNeighbors;
    } else {
      // Enemy king — reward if we're attacking it
      if (friendlyAttackSet.has(key)) {
        const attackerCount = (team === 'player' ? attacks.playerAttackCount : attacks.enemyAttackCount).get(key) ?? 1;
        score += 200 * attackerCount;
      }
      // Reward if enemy king's neighbors are under our attack
      let pressuredNeighbors = 0;
      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          const nk = `${piece.pos.col + dc},${piece.pos.row + dr}`;
          if (friendlyAttackSet.has(nk)) pressuredNeighbors++;
        }
      }
      score += 15 * pressuredNeighbors;
    }
  }
  return score;
}

/** Threats: reward attacking enemy pieces, penalize own pieces under attack. */
function evalThreats(
  snap: BoardSnapshot,
  team: Team,
  attacks: AttackMaps,
): number {
  let score = 0;
  const ourAttacks = team === 'player' ? attacks.playerAttacks : attacks.enemyAttacks;
  const theirAttacks = team === 'player' ? attacks.enemyAttacks : attacks.playerAttacks;

  for (const piece of snap.pieces) {
    const key = `${piece.pos.col},${piece.pos.row}`;
    const value = PIECE_VALUES[piece.type] || 100; // King treated as 100 here

    if (piece.team === team) {
      // Our piece is under threat — penalty proportional to value
      if (theirAttacks.has(key)) {
        score -= value * 0.5;
      }
    } else {
      // Enemy piece is under our threat — bonus
      if (ourAttacks.has(key)) {
        score += value * 0.5;
      }
    }
  }
  return score;
}

/** Defense: reward pieces that are protected by friendly pieces. */
function evalDefense(
  snap: BoardSnapshot,
  team: Team,
  attacks: AttackMaps,
): number {
  let score = 0;
  const ourAttacks = team === 'player' ? attacks.playerAttacks : attacks.enemyAttacks;
  const theirAttacks = team === 'player' ? attacks.enemyAttacks : attacks.playerAttacks;

  for (const piece of snap.pieces) {
    if (piece.team !== team) continue;
    const key = `${piece.pos.col},${piece.pos.row}`;
    // Piece is defended by a friendly piece
    if (ourAttacks.has(key)) {
      score += 20;
      // Extra value if it's also under threat (defended AND attacked = OK)
      if (theirAttacks.has(key)) score += 15;
    }
  }
  return score;
}

/** Advancement: reward pieces pushed toward enemy side. */
function evalAdvancement(snap: BoardSnapshot, team: Team): number {
  let score = 0;
  for (const piece of snap.pieces) {
    if (piece.type === 'King') continue; // King shouldn't rush forward
    if (piece.team === team) {
      // Enemy team advances toward high rows, player toward low rows
      const advancement = team === 'enemy'
        ? piece.pos.row  // enemy advances downward (higher row)
        : (BOARD_ROWS - 1 - piece.pos.row); // player advances upward
      score += advancement * 10;
    } else {
      const advancement = piece.team === 'enemy'
        ? piece.pos.row
        : (BOARD_ROWS - 1 - piece.pos.row);
      score -= advancement * 10;
    }
  }
  return score;
}

/** Center control: reward pieces near the center of the board. */
function evalCenterControl(snap: BoardSnapshot, team: Team): number {
  const centerCol = (BOARD_COLS - 1) / 2;
  const centerRow = (BOARD_ROWS - 1) / 2;
  let score = 0;

  for (const piece of snap.pieces) {
    const distFromCenter =
      Math.abs(piece.pos.col - centerCol) + Math.abs(piece.pos.row - centerRow);
    const centrality = Math.max(0, 5 - distFromCenter); // 0-5 scale
    score += piece.team === team ? centrality * 5 : -centrality * 5;
  }
  return score;
}

/** Mobility: number of valid moves available (approximated from attack map). */
function evalMobility(
  _snap: BoardSnapshot,
  team: Team,
  attacks: AttackMaps,
): number {
  const ourSquares = team === 'player' ? attacks.playerAttacks.size : attacks.enemyAttacks.size;
  const theirSquares = team === 'player' ? attacks.enemyAttacks.size : attacks.playerAttacks.size;
  return (ourSquares - theirSquares) * 3;
}

// -----------------------------------------------------------------
// Main evaluation function
// -----------------------------------------------------------------

/**
 * Evaluate a board position from the perspective of the given team.
 * Positive = good for team, negative = bad.
 */
export function evaluatePosition(
  snap: BoardSnapshot,
  team: Team,
  weights: Partial<EvalWeights> = {},
): number {
  const w = { ...DEFAULT_WEIGHTS, ...weights };

  // Check for king capture (terminal states)
  const ourKing = snap.pieces.find(p => p.type === 'King' && p.team === team);
  const enemyKing = snap.pieces.find(p => p.type === 'King' && p.team !== team);

  if (!ourKing) return -99999;  // We lost our king
  if (!enemyKing) return 99999; // We captured their king

  const attacks = buildAttackMaps(snap);

  return (
    w.material * evalMaterial(snap, team) +
    w.kingSafety * evalKingSafety(snap, team, attacks) +
    w.threats * evalThreats(snap, team, attacks) +
    w.defense * evalDefense(snap, team, attacks) +
    w.advancement * evalAdvancement(snap, team) +
    w.centerControl * evalCenterControl(snap, team) +
    w.mobility * evalMobility(snap, team, attacks)
  );
}
